import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LoadingController, NavController, IonicModule } from '@ionic/angular';
import { NgxDatatableModule, ColumnMode, SelectionType } from '@swimlane/ngx-datatable';
import _ from 'lodash';

import { Project } from 'src/models/projects';
import { AuthService } from 'src/services/auth.service';
import { JsmService, type JsmCredential, type JsmServiceDesk } from 'src/services/jsm.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, NgxDatatableModule],
  selector: 'app-jsm-channel-create',
  templateUrl: './jsm-channel-create.component.html',
  styleUrls: ['./jsm-channel-create.component.scss'],
})
export class JsmChannelCreateComponent implements OnInit {
  isLoading = false;
  searchText: string;
  unconnectedServiceDesks: JsmServiceDesk[] = [];
  selectedServiceDesks: JsmServiceDesk[] = [];
  filteredServiceDesks: JsmServiceDesk[] = [];
  isLoadingServiceDesks = false;
  connectedCredentials: JsmCredential[] = [];
  selectedCredential?: JsmCredential;
  project: Partial<Project>;
  isLastPage = false;
  currentStart = 0;
  showHelpBanner = false;

  ColumnMode = ColumnMode;
  SelectionType = SelectionType;

  constructor(
    public authService: AuthService,
    private msgSrvc: MessageService,
    private navController: NavController,
    private loadingController: LoadingController,
    private jsmService: JsmService,
    private searchablePopoverService: SearchablePopoverService,
  ) {}

  async ngOnInit() {
    this.isLoading = true;

    if (this.authService.selectedProject) {
      this.setProject(this.authService.selectedProject);
    } else if (this.authService.projects?.length) {
      this.setProject(this.authService.projects[0]);
    }

    this.connectedCredentials = await this.jsmService.getConnectedCredentials();

    if (this.connectedCredentials.length > 0) {
      this.selectedCredential = this.connectedCredentials[0];
      await this.loadServiceDesks();
    }

    this.isLoading = false;
  }

  setProject(project: Partial<Project>) {
    this.project = project;
  }

  async loadServiceDesks(clear = false) {
    if (!this.selectedCredential) {
      this.msgSrvc.show('Please select a JSM instance');
      return;
    }

    this.isLoadingServiceDesks = true;

    if (clear) {
      this.unconnectedServiceDesks = [];
      this.currentStart = 0;
      this.isLastPage = false;
    }

    try {
      const response = await this.jsmService.getServiceDesks(this.selectedCredential.jsmInstanceId, this.currentStart);

      // Append new service desks to existing ones
      this.unconnectedServiceDesks.push(...response.values);

      this.currentStart += response.size;
      this.isLastPage = response.isLastPage;

      this.showHelpBanner = this.unconnectedServiceDesks.length === 0;

      this.handleFilter();
    } catch (e) {
      this.msgSrvc.showError(e);
    }

    this.isLoadingServiceDesks = false;
  }

  deselectAll() {
    this.selectedServiceDesks = [];
  }

  selectAll() {
    this.selectedServiceDesks = [...this.filteredServiceDesks];
  }

  async save() {
    if (!this.selectedServiceDesks?.length) {
      this.msgSrvc.show('Please select service desks by ticking the checkboxes');
      return;
    }

    if (!this.project?.id) {
      this.msgSrvc.show('Please select a project');
      return;
    }

    if (!this.selectedCredential) {
      this.msgSrvc.show('Please select a JSM instance');
      return;
    }

    this.isLoading = true;

    try {
      const loading = await this.loadingController.create({
        message: 'Connecting JSM service desks...',
      });

      await loading.present();

      const result = await this.jsmService.bulkCreateChannels(
        this.project.id,
        this.selectedCredential.id,
        this.selectedCredential.jsmInstanceId,
        this.selectedServiceDesks.map((sd) => ({
          id: sd.id,
          name: sd.projectName,
        })),
      );

      if (result.failed > 0) {
        this.msgSrvc.show(
          `Created ${result.created} channel(s), but ${result.failed} failed. Check console for details.`,
        );
        console.error('Failed channels:', result.errors);
      } else if (this.selectedServiceDesks.length === 1 && result.results.length === 1) {
        // Navigate to the single created channel
        this.navController.navigateRoot(`/dashboard/customers/${result.results[0].channelId}`);
      }

      await loading.dismiss();
    } catch (err) {
      this.msgSrvc.show(err.message || 'Failed to create JSM channels');
    } finally {
      this.isLoading = false;
    }
  }

  handleFilter() {
    const term = this.searchText?.toLowerCase();

    this.filteredServiceDesks = this.unconnectedServiceDesks.filter((serviceDesk) => {
      if (!term) {
        return true;
      }

      return (
        serviceDesk.projectName.toLowerCase().includes(term) || serviceDesk.projectKey.toLowerCase().includes(term)
      );
    });
  }

  onSelect({ selected }) {
    this.selectedServiceDesks.splice(0, this.selectedServiceDesks.length);
    this.selectedServiceDesks.push(...selected);
  }

  goBack() {
    this.navController.navigateRoot('dashboard/channels');
  }

  toggleRowSelected(row: JsmServiceDesk) {
    const index = this.selectedServiceDesks.findIndex((sd) => sd.id === row.id);
    if (index > -1) {
      this.selectedServiceDesks.splice(index, 1);
    } else {
      this.selectedServiceDesks.push(row);
    }
  }

  selectProject() {
    this.searchablePopoverService.projects({
      event,
      showNoneOption: false,
      selectedProjectId: this.project?.id,
      callback: async (_id: string, _name: string, object: any) => {
        this.setProject(object);
        // Reload service desks when project changes to filter connected ones
        await this.loadServiceDesks(true);
      },
    });
  }

  async onCredentialChange() {
    // Clear and reload service desks when credential changes
    await this.loadServiceDesks(true);
  }
}
